import { isAxiosError, type AxiosInstance } from 'axios';

export class SchoolSaveUnconfirmedError extends Error {
  constructor(schoolId: string, readonly originalError: unknown) {
    super(
      `Could not confirm whether school "${schoolId}" was saved. Check that school before retrying or changing its ID.`,
    );
  }
}

const matches = (actual: unknown, expected: unknown, key: string): boolean => {
  // HighSchoolCreate/Update normalize primary colors to uppercase.
  if (
    (key === 'primaryColor' || key === 'primary_color') &&
    typeof actual === 'string' &&
    typeof expected === 'string'
  ) {
    return actual === expected.trim().toUpperCase();
  }
  // The legacy form sends numeric fields as strings; the API returns numbers.
  if (
    typeof actual === 'number' &&
    typeof expected === 'string' &&
    expected.trim() !== '' &&
    [
      'lat',
      'lng',
      'students_count',
      'studentsCount',
      'chat_minimum_depth',
      'chatMinimumDepth',
    ].includes(key)
  ) {
    return actual === Number(expected);
  }
  if (Array.isArray(expected)) {
    return (
      Array.isArray(actual) &&
      actual.length === expected.length &&
      expected.every((value, index) => matches(actual[index], value, key))
    );
  }
  return actual === expected;
};

/** Recover a lost write response only after reading back the submitted values. */
export const saveSchoolWithVerification = async <T extends object>(
  client: AxiosInstance,
  method: 'post' | 'put' | 'patch',
  schoolId: string,
  school: Record<string, unknown>,
  options: {
    path?: string;
    verificationValues?: Record<string, unknown>;
  } = {},
) => {
  const schoolPath = `v2/schools/${encodeURIComponent(schoolId)}`;
  try {
    return await client[method]<T>(
      options.path ?? (method === 'post' ? 'v2/schools' : schoolPath),
      school,
    );
  } catch (error) {
    // Validation, conflicts and authorization failures must remain failures.
    if (
      !isAxiosError(error) ||
      error.code === 'ERR_CANCELED' ||
      (error.response &&
        error.response.status < 500 &&
        error.response.status !== 408)
    ) {
      throw error;
    }

    // Allow a little time for an in-flight write or replica to catch up. Never
    // repeat the write: a second create could duplicate an already saved school.
    for (const delay of [0, 500, 1000]) {
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      try {
        const response = await client.get<T>(schoolPath, {
          // CloudFront keys on query parameters. Avoid custom cache headers,
          // which the API's cross-origin policy does not allow.
          params: { for_dashboard: true, _save_check: Date.now() },
          timeout: 5000,
        });
        const saved = response.data as Record<string, unknown>;
        if (
          (saved.schoolName ?? saved.school_name) === schoolId &&
          Object.entries(options.verificationValues ?? school).every(
            ([key, value]) =>
              // Address is a legacy geocoder input, not a persisted API field.
              key === 'address' ||
              value === undefined ||
              matches(saved[key], value, key),
          )
        ) {
          return response;
        }
      } catch {
        // A failed read does not prove that the preceding write failed.
      }
    }
    throw new SchoolSaveUnconfirmedError(schoolId, error);
  }
};
